import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Upload, Clock, Loader2, CheckCircle2, XCircle, AlertCircle,
    Activity, RefreshCw, Archive, Trash2, MoreHorizontal, UserCheck,
    RotateCcw, AlertTriangle,
} from 'lucide-react';
import { ordersApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';


/**
 * Sony Upload Jobs — lightweight admin overview.
 *
 * Reads from the EXISTING ``sony_upload_jobs`` collection via
 * ``GET /api/admin/sony-upload/jobs``. Deliberately NOT a job queue
 * UI — clicking a row navigates to the parent order, where the
 * existing SonyUploadPanel already renders all the deep controls
 * (retry / view JSON / status history / masters).
 */

const STATUS_META = {
    queued:      { label: 'Queued',     color: 'bg-blue-100 text-blue-700 border-blue-300',         icon: Clock },
    processing:  { label: 'Processing', color: 'bg-amber-100 text-amber-700 border-amber-300',      icon: Loader2 },
    completed:   { label: 'Completed',  color: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle2 },
    failed:      { label: 'Failed',     color: 'bg-red-100 text-red-700 border-red-300',            icon: XCircle },
    stale:       { label: 'Stale',      color: 'bg-red-100 text-red-700 border-red-300',            icon: AlertCircle },
};


const STEP_LABELS = {
    starting_worker:    'Starting worker',
    creating_project:   'Creating project',
    creating_master:    'Creating master',
    downloading_files:  'Downloading files',
    uploading_files:    'Uploading files',
    waiting_for_sony:   'Waiting for Sony',
};


const StatusBadge = ({ row }) => {
    // ``is_stale`` supersedes the raw status pill so admins immediately
    // spot a silent worker instead of chasing "processing" rows.
    const key = row.is_stale ? 'stale' : (row.status || 'queued');
    const meta = STATUS_META[key] || STATUS_META.queued;
    const Icon = meta.icon;
    return (
        <div className="flex items-center gap-1.5 flex-wrap">
            <Badge className={`${meta.color} border`} data-testid={`sony-jobs-row-status-${key}`}>
                <Icon className={`h-3 w-3 mr-1 ${key === 'processing' ? 'animate-spin' : ''}`} />
                {meta.label}
            </Badge>
            {row.completed_manually && (
                <Badge
                    variant="outline"
                    className="bg-indigo-50 text-indigo-700 border-indigo-200"
                    title={
                        row.manually_completed_by_name
                            ? `Manually completed by ${row.manually_completed_by_name}${row.manual_completion_note ? ` — ${row.manual_completion_note}` : ''}`
                            : 'Completed manually — Sony upload was finished outside FLOW'
                    }
                    data-testid="sony-jobs-row-manual"
                >
                    <UserCheck className="h-3 w-3 mr-1" /> Completed manually
                </Badge>
            )}
            {row.dismissed_at && (
                <Badge
                    variant="outline"
                    className="bg-slate-100 text-slate-600 border-slate-300"
                    title={
                        row.dismissed_by_name
                            ? `Archived by ${row.dismissed_by_name}${row.dismiss_reason ? ` — ${row.dismiss_reason}` : ''}`
                            : 'Archived — no longer counted in Needs Attention'
                    }
                    data-testid="sony-jobs-row-archived"
                >
                    <Archive className="h-3 w-3 mr-1" /> Archived
                </Badge>
            )}
        </div>
    );
};


const fmtTs = (iso) => {
    if (!iso) return '—';
    return String(iso).replace('T', ' ').slice(0, 16);
};


const relAgo = (iso) => {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        const s = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
        if (s < 60) return `${s}s ago`;
        if (s < 3600) return `${Math.round(s / 60)}m ago`;
        return `${Math.round(s / 3600)}h ago`;
    } catch { return null; }
};


const FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'queued', label: 'Queued' },
    { key: 'processing', label: 'Processing' },
    { key: 'failed', label: 'Failed' },
    { key: 'stale', label: 'Stale' },
    { key: 'completed', label: 'Completed' },
];


export default function SonyUploadJobsPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const currentFilter = searchParams.get('status') || 'all';
    const [rows, setRows] = useState([]);
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [deleteTarget, setDeleteTarget] = useState(null); // { id, order_number }
    const [busyRowId, setBusyRowId] = useState(null);
    // Manual-completion confirmation dialog state.
    const [manualTarget, setManualTarget] = useState(null); // { id, project_name, order_number, error_message }
    const [manualNote, setManualNote] = useState('');
    // "Reset after Sony project deletion" dialog state.
    const [resetTarget, setResetTarget] = useState(null); // row
    const [resetNote, setResetNote] = useState('');
    const [resetConfirmed, setResetConfirmed] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await ordersApi.listSonyUploadJobs(
                currentFilter === 'all' ? null : currentFilter,
            );
            setRows(res.data?.rows || []);
            setCounts(res.data?.counts || {});
        } catch {
            toast.error('Could not load Sony upload jobs');
        } finally {
            setLoading(false);
        }
    }, [currentFilter]);

    useEffect(() => { load(); }, [load]);

    // Poll every 10s so admins see the queue advance without a
    // manual refresh. Cheap — endpoint reads one collection.
    useEffect(() => {
        const t = setInterval(load, 10000);
        return () => clearInterval(t);
    }, [load]);

    const canDismiss = (r) => !r.dismissed_at && (r.status === 'failed' || (r.status === 'processing' && r.is_stale));
    const canDelete = (r) => r.status === 'failed' || (r.status === 'processing' && r.is_stale);
    // Manual completion is for the exact recovery case where FLOW's
    // worker failed but the operator uploaded manually via Aspera.
    // Completed/queued/healthy-processing jobs are not eligible —
    // backend enforces the same rules; this just hides the menu item
    // when it can't be used.
    const canCompleteManually = (r) =>
        !r.completed_manually
        && (r.status === 'failed' || (r.status === 'processing' && r.is_stale));
    // Reset after Sony project deletion — backend computes precise
    // eligibility (failed job, every upload unit still not_started and
    // unflagged) and returns it on the row.
    const canResetAfterProjectDeletion = (r) => !!r.reset_after_project_deletion_eligible;

    const handleDismiss = async (row) => {
        setBusyRowId(row.id);
        try {
            await ordersApi.dismissSonyUploadJob(row.id);
            toast.success(`Job ${row.project_name || row.id.slice(0, 8)} archived — removed from Needs Attention`);
            await load();
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Failed to archive job';
            toast.error(msg);
        } finally {
            setBusyRowId(null);
        }
    };

    const handleDeleteConfirmed = async () => {
        if (!deleteTarget) return;
        const row = deleteTarget;
        setDeleteTarget(null);
        setBusyRowId(row.id);
        try {
            await ordersApi.deleteSonyUploadJob(row.id);
            toast.success('Sony upload job permanently deleted');
            await load();
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Failed to delete job';
            toast.error(msg);
        } finally {
            setBusyRowId(null);
        }
    };

    const handleCompleteManuallyConfirmed = async () => {
        if (!manualTarget) return;
        const row = manualTarget;
        const note = manualNote.trim() || null;
        setManualTarget(null);
        setManualNote('');
        setBusyRowId(row.id);
        try {
            await ordersApi.completeSonyUploadManually(row.id, note);
            toast.success(
                `Job ${row.project_name || row.id.slice(0, 8)} marked completed manually — ` +
                'parent order master status moved to Uploaded.'
            );
            await load();
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Failed to mark completed manually';
            toast.error(msg);
        } finally {
            setBusyRowId(null);
        }
    };

    const handleResetAfterProjectDeletionConfirmed = async () => {
        if (!resetTarget || !resetConfirmed) return;
        const row = resetTarget;
        const note = resetNote.trim() || null;
        setResetTarget(null);
        setResetNote('');
        setResetConfirmed(false);
        setBusyRowId(row.id);
        try {
            await ordersApi.resetSonyUploadAfterProjectDeletion(row.id, note);
            toast.success(
                `Job ${row.project_name || row.id.slice(0, 8)} reset and requeued — ` +
                'the worker will recreate the Sony project and upload both layers.'
            );
            await load();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = (detail && typeof detail === 'object')
                ? (detail.message || 'Reset rejected')
                : (detail || 'Failed to reset job');
            toast.error(msg);
        } finally {
            setBusyRowId(null);
        }
    };

    return (
        <div className="max-w-7xl mx-auto p-6 space-y-4" data-testid="sony-upload-jobs-page">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-semibold text-slate-800 flex items-center gap-2">
                        <Upload className="h-6 w-6 text-indigo-600" />
                        Sony Upload Jobs
                    </h1>
                    <p className="text-sm text-slate-600 mt-1">
                        Operational overview of every Sony master upload job. Click a row to open the source order.
                    </p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={load}
                    disabled={loading}
                    data-testid="sony-jobs-refresh"
                >
                    <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </Button>
            </div>

            {/* Summary chips — Queued / Processing / Failed / Stale */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="sony-jobs-summary">
                {[
                    { key: 'queued', label: 'Queued', tone: 'bg-blue-50 border-blue-200 text-blue-700' },
                    { key: 'processing', label: 'Processing', tone: 'bg-amber-50 border-amber-200 text-amber-700' },
                    { key: 'failed', label: 'Failed', tone: 'bg-red-50 border-red-200 text-red-700' },
                    { key: 'stale', label: 'Stale', tone: 'bg-red-50 border-red-200 text-red-700' },
                    { key: 'completed', label: 'Completed', tone: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
                ].map((c) => (
                    <button
                        key={c.key}
                        onClick={() => setSearchParams(c.key === currentFilter ? {} : { status: c.key })}
                        className={`rounded-lg border p-3 text-left transition ${c.tone} ${currentFilter === c.key ? 'ring-2 ring-offset-1 ring-slate-400' : 'hover:opacity-90'}`}
                        data-testid={`sony-jobs-summary-${c.key}`}
                    >
                        <div className="text-xs font-medium uppercase tracking-wide">{c.label}</div>
                        <div className="text-2xl font-semibold mt-1">{counts[c.key] ?? 0}</div>
                    </button>
                ))}
            </div>

            {/* Filter tabs */}
            <div className="flex flex-wrap gap-1 border-b border-slate-200" data-testid="sony-jobs-filters">
                {FILTERS.map((f) => {
                    const active = currentFilter === f.key;
                    return (
                        <button
                            key={f.key}
                            onClick={() => setSearchParams(f.key === 'all' ? {} : { status: f.key })}
                            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition ${
                                active
                                    ? 'border-indigo-600 text-indigo-700'
                                    : 'border-transparent text-slate-600 hover:text-slate-800'
                            }`}
                            data-testid={`sony-jobs-filter-${f.key}`}
                        >
                            {f.label}
                            <span className="ml-1 text-xs text-slate-400">
                                ({counts[f.key] ?? 0})
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* Table */}
            <div className="rounded-lg border border-slate-200 overflow-x-auto" data-testid="sony-jobs-table-wrap">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500 tracking-wide">
                        <tr>
                            <th className="px-3 py-2">Status</th>
                            <th className="px-3 py-2">Order #</th>
                            <th className="px-3 py-2">Catalogue</th>
                            <th className="px-3 py-2">Sony project</th>
                            <th className="px-3 py-2">Media type</th>
                            <th className="px-3 py-2">Created</th>
                            <th className="px-3 py-2">Started</th>
                            <th className="px-3 py-2">Step</th>
                            <th className="px-3 py-2">Last heartbeat</th>
                            <th className="px-3 py-2 text-right">Attempt</th>
                            <th className="px-3 py-2">Error / result</th>
                            <th className="px-3 py-2 w-8"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100" data-testid="sony-jobs-table-body">
                        {loading && rows.length === 0 && (
                            <tr><td colSpan={12} className="px-3 py-6 text-center text-slate-500">
                                <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
                            </td></tr>
                        )}
                        {!loading && rows.length === 0 && (
                            <tr><td colSpan={12} className="px-3 py-6 text-center text-slate-400" data-testid="sony-jobs-empty">
                                No Sony upload jobs match this filter.
                            </td></tr>
                        )}
                        {rows.map((r) => (
                            <tr
                                key={r.id}
                                onClick={() => r.order_id && navigate(`/orders/${r.order_id}`)}
                                className={`${r.order_id ? 'cursor-pointer' : 'cursor-default'} hover:bg-indigo-50/40 ${r.dismissed_at ? 'opacity-70' : ''}`}
                                data-testid={`sony-jobs-row-${r.id}`}
                            >
                                <td className="px-3 py-2"><StatusBadge row={r} /></td>
                                <td className="px-3 py-2 font-mono">{r.order_number ?? '—'}</td>
                                <td className="px-3 py-2 font-mono">{r.catalogue_number ?? '—'}</td>
                                <td className="px-3 py-2 font-mono">{r.project_name || '—'}</td>
                                <td className="px-3 py-2 text-slate-600">
                                    {r.media_type || '—'}
                                    {r.master_count > 1 && (
                                        <span className="ml-1 text-slate-400 text-xs">× {r.master_count}</span>
                                    )}
                                </td>
                                <td className="px-3 py-2 text-slate-600">{fmtTs(r.created_at)}</td>
                                <td className="px-3 py-2 text-slate-600">{fmtTs(r.processing_started_at)}</td>
                                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                                    <Activity className="h-3 w-3 inline mr-1 text-slate-400" />
                                    {STEP_LABELS[r.current_step] || r.current_step || '—'}
                                </td>
                                <td className={`px-3 py-2 whitespace-nowrap ${r.is_stale ? 'text-red-600 font-medium' : 'text-slate-600'}`}>
                                    {r.last_heartbeat_at
                                        ? `${fmtTs(r.last_heartbeat_at)} (${relAgo(r.last_heartbeat_at)})`
                                        : '—'}
                                </td>
                                <td className="px-3 py-2 text-right text-slate-700">{r.attempt || 0}</td>
                                <td className="px-3 py-2 text-slate-600 max-w-xs">
                                    {r.error_message ? (
                                        <span className="text-red-600 line-clamp-2" title={r.error_message}>
                                            {r.error_code ? <code className="text-xs mr-1">{r.error_code}</code> : null}
                                            {r.error_message}
                                        </span>
                                    ) : (r.status === 'completed'
                                        ? <span className="text-emerald-600">Received by Sony</span>
                                        : <span className="text-slate-400">—</span>)}
                                </td>
                                <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                                    {(canDismiss(r) || canDelete(r) || canCompleteManually(r) || canResetAfterProjectDeletion(r)) && (
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-7 w-7"
                                                    disabled={busyRowId === r.id}
                                                    data-testid={`sony-jobs-row-actions-${r.id}`}
                                                    aria-label="Row actions"
                                                >
                                                    {busyRowId === r.id
                                                        ? <Loader2 className="h-4 w-4 animate-spin" />
                                                        : <MoreHorizontal className="h-4 w-4" />}
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="w-64">
                                                {canResetAfterProjectDeletion(r) && (
                                                    <DropdownMenuItem
                                                        onClick={() => { setResetNote(''); setResetConfirmed(false); setResetTarget(r); }}
                                                        data-testid={`sony-jobs-row-reset-project-deleted-${r.id}`}
                                                    >
                                                        <RotateCcw className="h-4 w-4 mr-2 text-amber-600" />
                                                        Reset after project deletion
                                                    </DropdownMenuItem>
                                                )}
                                                {canResetAfterProjectDeletion(r) && (canCompleteManually(r) || canDismiss(r) || canDelete(r)) && <DropdownMenuSeparator />}
                                                {canCompleteManually(r) && (
                                                    <DropdownMenuItem
                                                        onClick={() => { setManualNote(''); setManualTarget(r); }}
                                                        data-testid={`sony-jobs-row-complete-manually-${r.id}`}
                                                    >
                                                        <UserCheck className="h-4 w-4 mr-2 text-indigo-600" />
                                                        Mark as completed manually
                                                    </DropdownMenuItem>
                                                )}
                                                {canCompleteManually(r) && (canDismiss(r) || canDelete(r)) && <DropdownMenuSeparator />}
                                                {canDismiss(r) && (
                                                    <DropdownMenuItem
                                                        onClick={() => handleDismiss(r)}
                                                        data-testid={`sony-jobs-row-dismiss-${r.id}`}
                                                    >
                                                        <Archive className="h-4 w-4 mr-2" />
                                                        Dismiss / Archive
                                                    </DropdownMenuItem>
                                                )}
                                                {canDismiss(r) && canDelete(r) && <DropdownMenuSeparator />}
                                                {canDelete(r) && (
                                                    <DropdownMenuItem
                                                        className="text-red-600 focus:text-red-700"
                                                        onClick={() => setDeleteTarget(r)}
                                                        data-testid={`sony-jobs-row-delete-${r.id}`}
                                                    >
                                                        <Trash2 className="h-4 w-4 mr-2" />
                                                        Delete permanently
                                                    </DropdownMenuItem>
                                                )}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <AlertDialog
                open={!!manualTarget}
                onOpenChange={(open) => { if (!open) { setManualTarget(null); setManualNote(''); } }}
            >
                <AlertDialogContent data-testid="sony-jobs-manual-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Mark this Sony upload as completed manually?</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-3 text-sm">
                                {manualTarget && (
                                    <p>
                                        Use this only when the Sony upload was finished <strong>outside FLOW</strong>
                                        {' '}(operator ran it directly in Aspera). This action is for
                                        {' '}<strong>{manualTarget.project_name || manualTarget.id}</strong>
                                        {manualTarget.order_number ? ` (order #${manualTarget.order_number})` : ''}.
                                    </p>
                                )}
                                <ul className="list-disc pl-5 space-y-1 text-slate-600">
                                    <li>Job status → <strong>Completed</strong></li>
                                    <li><em>completed_manually</em> flag set, admin + timestamp recorded</li>
                                    <li>Previous failure, attempts and status history <strong>preserved</strong> for audit</li>
                                    <li>Order master status moves to <strong>Uploaded</strong> (Sony confirmation can still later promote to <em>Received</em>)</li>
                                </ul>
                                {manualTarget?.error_message && (
                                    <div className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                                        Original failure (kept for audit): {manualTarget.error_code ? <code className="mr-1">{manualTarget.error_code}</code> : null}
                                        {manualTarget.error_message}
                                    </div>
                                )}
                                <div className="space-y-1 pt-1">
                                    <Label htmlFor="manual-note" className="text-xs">Note (optional — e.g. ticket reference, Aspera batch id)</Label>
                                    <Textarea
                                        id="manual-note"
                                        value={manualNote}
                                        onChange={(e) => setManualNote(e.target.value)}
                                        rows={2}
                                        placeholder="e.g. Uploaded via Aspera manually – ticket #482"
                                        className="text-sm"
                                        data-testid="sony-jobs-manual-note"
                                    />
                                </div>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="sony-jobs-manual-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleCompleteManuallyConfirmed}
                            className="bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-600"
                            data-testid="sony-jobs-manual-confirm-btn"
                        >
                            <UserCheck className="h-4 w-4 mr-2" />
                            Yes, mark completed manually
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog
                open={!!resetTarget}
                onOpenChange={(open) => { if (!open) { setResetTarget(null); setResetNote(''); setResetConfirmed(false); } }}
            >
                <AlertDialogContent data-testid="sony-jobs-reset-project-deleted-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Reset this job after the Sony project was deleted?</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-3 text-sm">
                                {resetTarget && (
                                    <p>
                                        Use this only when the Sony project was <strong>manually deleted</strong> in the
                                        {' '}Sony portal and verified gone. This rewinds
                                        {' '}<strong>{resetTarget.project_name || resetTarget.id}</strong>
                                        {resetTarget.order_number ? ` (order #${resetTarget.order_number})` : ''}
                                        {' '}back to the start and requeues it.
                                    </p>
                                )}
                                <div className="rounded border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-800 flex gap-2" data-testid="sony-jobs-reset-duplicate-warning">
                                    <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                                    <span>
                                        <strong>Duplicate-project risk:</strong> if the Sony project has <em>not</em> actually been
                                        deleted, the worker will create a <strong>second, duplicate project</strong> in Sony on the
                                        next run. Only confirm once you have verified the original project is gone.
                                    </span>
                                </div>
                                <ul className="list-disc pl-5 space-y-1 text-slate-600">
                                    <li>Workflow checkpoint → <strong>not started</strong>, job status → <strong>Queued</strong></li>
                                    <li>Job ID, all files, upload units and operation mode <strong>preserved</strong></li>
                                    <li>Reset + your confirmation recorded in <strong>status history</strong></li>
                                    <li>The worker is <strong>not</strong> started by this action — it picks the job up on its next poll</li>
                                </ul>
                                <label className="flex items-start gap-2 pt-1 cursor-pointer select-none">
                                    <Checkbox
                                        checked={resetConfirmed}
                                        onCheckedChange={(v) => setResetConfirmed(v === true)}
                                        className="mt-0.5"
                                        data-testid="sony-jobs-reset-confirm-checkbox"
                                    />
                                    <span className="text-sm text-slate-700">
                                        I confirm the Sony project has been <strong>deleted</strong> and verified gone.
                                    </span>
                                </label>
                                <div className="space-y-1 pt-1">
                                    <Label htmlFor="reset-note" className="text-xs">Note (optional — e.g. which project was deleted, ticket reference)</Label>
                                    <Textarea
                                        id="reset-note"
                                        value={resetNote}
                                        onChange={(e) => setResetNote(e.target.value)}
                                        rows={2}
                                        placeholder="e.g. Deleted project PRJ-123 in Sony portal, verified gone – ticket #517"
                                        className="text-sm"
                                        data-testid="sony-jobs-reset-note"
                                    />
                                </div>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="sony-jobs-reset-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleResetAfterProjectDeletionConfirmed}
                            disabled={!resetConfirmed}
                            className="bg-amber-600 hover:bg-amber-700 focus:ring-amber-600 disabled:opacity-50 disabled:pointer-events-none"
                            data-testid="sony-jobs-reset-confirm-btn"
                        >
                            <RotateCcw className="h-4 w-4 mr-2" />
                            Reset &amp; requeue
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
                <AlertDialogContent data-testid="sony-jobs-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Permanently delete this Sony upload job?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {deleteTarget && (
                                <>
                                    You are about to permanently delete the record for
                                    {' '}<strong>{deleteTarget.project_name || deleteTarget.id}</strong>
                                    {deleteTarget.order_number ? ` (order #${deleteTarget.order_number})` : ''}.
                                    <br /><br />
                                    This removes the audit trail for the job. If you only need to hide the job
                                    from Needs Attention, use <em>Dismiss / Archive</em> instead.
                                </>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="sony-jobs-delete-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteConfirmed}
                            className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                            data-testid="sony-jobs-delete-confirm-btn"
                        >
                            Delete permanently
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
